import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { topics } from "@/mocks/topics";
import { discussions } from "@/mocks/discussions";
import type { ContentTopic } from "@/types/content";
import usePageSeo from "@/hooks/usePageSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import DiscussionCard from "@/components/feature/DiscussionCard";
import TopicCard, { formatCount } from "@/components/feature/TopicCard";

const ORIGIN = "https://ftosc.cn";

export default function TopicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const topic = useMemo(() => topics.find((item) => item.id === id), [id]);

  const related = useMemo(() => {
    if (!topic) {
      return [];
    }
    return topic.relatedIds
      .map((relatedId) => topics.find((item) => item.id === relatedId))
      .filter((item): item is ContentTopic => Boolean(item))
      .slice(0, 3);
  }, [topic]);

  const topicDiscussions = useMemo(
    () => discussions.filter((item) => item.topicId === id),
    [id]
  );

  const canonicalPath = `/topic/${id ?? ""}`;

  usePageSeo({
    title: topic ? `${topic.title} - 瓜田吃瓜` : "话题不存在 - 瓜田吃瓜",
    description: topic
      ? `${topic.summary}（瓜田吃瓜话题整理，含事件时间线与讨论区观点交锋）`
      : "该话题暂时无法找到，返回话题榜继续围观。",
    keywords: topic ? `${topic.category},${topic.tags.join(",")},瓜田吃瓜` : "瓜田吃瓜",
    path: canonicalPath,
    ogType: "article",
    image: topic?.cover,
    robots: topic ? "index,follow,max-image-preview:large" : "noindex,follow",
    jsonLd: topic
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: topic.title,
            description: topic.summary,
            image: topic.cover,
            articleSection: topic.category,
            datePublished: topic.publishedAt,
            dateModified: topic.publishedAt,
            inLanguage: "zh-CN",
            keywords: topic.tags.join(","),
            author: { "@type": "Person", name: topic.author },
            publisher: {
              "@type": "Organization",
              name: "瓜田吃瓜",
              url: `${ORIGIN}/`,
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": `${ORIGIN}${canonicalPath}` },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
              { "@type": "ListItem", position: 2, name: "话题榜", item: `${ORIGIN}/topics` },
              { "@type": "ListItem", position: 3, name: topic.title, item: `${ORIGIN}${canonicalPath}` },
            ],
          },
        ]
      : undefined,
  });

  if (!topic) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <i className="ri-emotion-unhappy-line text-4xl text-foreground-400" />
        <h1 className="mt-4 text-2xl font-bold text-foreground-950">这个瓜好像找不到了</h1>
        <p className="mt-3 text-sm text-foreground-600">话题可能已被合并或链接有误，去话题榜看看别的吧。</p>
        <Link
          to="/topics"
          className="mt-6 inline-flex items-center gap-1.5 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
        >
          返回话题榜
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full">
      <section className="w-full bg-background-200/50 border-b border-background-200/70">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <Breadcrumbs items={[{ label: "话题榜", to: "/topics" }, { label: topic.title }]} />
          <div className="mt-6 max-w-4xl">
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={`/topics?category=${topic.categoryId}`}
                className="px-3 py-1 rounded-full bg-primary-100 text-primary-800 text-xs hover:bg-primary-200 transition-colors whitespace-nowrap"
              >
                {topic.category}
              </Link>
              <span className="text-xs text-foreground-500 whitespace-nowrap">{topic.publishedAt}</span>
              <span className="text-xs text-foreground-500 whitespace-nowrap">阅读约 {topic.readTime} 分钟</span>
            </div>

            <h1 className="mt-5 text-2xl md:text-4xl font-bold text-foreground-950 leading-snug text-balance">
              {topic.title}
            </h1>

            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">{topic.summary}</p>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <i className="ri-user-line w-4 h-4 flex items-center justify-center" />
                {topic.author} · {topic.authorRole}
              </span>
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <i className="ri-fire-line text-primary-500 w-4 h-4 flex items-center justify-center" />
                热度 {formatCount(topic.heat)}
              </span>
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <i className="ri-eye-line w-4 h-4 flex items-center justify-center" />
                {formatCount(topic.views)} 阅读
              </span>
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <i className="ri-chat-3-line w-4 h-4 flex items-center justify-center" />
                {formatCount(topic.comments)} 条讨论
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          <article className="lg:col-span-2">
            <div className="w-full h-56 sm:h-80 rounded-lg overflow-hidden bg-background-200">
              <img
                src={topic.cover}
                alt={`${topic.title} - 话题配图`}
                title={`${topic.title} - 瓜田吃瓜话题详情`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-8 flex flex-col gap-5">
              {topic.content.map((paragraph, index) => (
                <p key={index} className="text-sm md:text-base leading-relaxed text-foreground-700">
                  {index === 0 ? (
                    <>
                      <strong className="font-bold text-foreground-950">{paragraph.slice(0, 22)}</strong>
                      {paragraph.slice(22)}
                    </>
                  ) : (
                    paragraph
                  )}
                </p>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-background-200/80 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500 mr-1">相关标签</span>
              {topic.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs border border-secondary-200 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </article>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg bg-background-200/50 border border-background-200/70 p-5">
              <h2 className="text-base font-bold text-foreground-950">事件时间线</h2>
              <ol className="mt-5 relative border-l border-background-300 pl-5 flex flex-col gap-5">
                {topic.timeline.map((item) => (
                  <li key={item.time} className="relative">
                    <span className="absolute -left-[26px] top-1 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-200" />
                    <span className="text-xs text-primary-700 font-medium">{item.time}</span>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg bg-accent-50 border border-accent-200/70 p-5">
              <h2 className="text-base font-bold text-accent-950">你也来说两句？</h2>
              <p className="mt-2 text-sm leading-relaxed text-accent-900/80">
                目前这个话题已有 {formatCount(topic.comments)} 条讨论，其中 {topicDiscussions.length} 条被编辑推荐。
              </p>
              <Link
                to="/discussion"
                className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-chat-smile-2-line w-4 h-4 flex items-center justify-center" />
                进入讨论区
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="w-full bg-background-200/50 border-y border-background-200/70">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-xl md:text-3xl font-bold text-foreground-950">围绕这个话题的观点交锋</h2>
              <p className="mt-2 text-sm text-foreground-600">不同立场同屏出现，欢迎你先看完再下判断。</p>
            </div>
            <Link
              to="/discussion"
              className="inline-flex items-center gap-1.5 text-sm text-accent-700 hover:text-accent-800 transition-colors whitespace-nowrap"
            >
              查看全部讨论
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {topicDiscussions.length > 0 ? (
              topicDiscussions.map((item) => <DiscussionCard key={item.id} item={item} />)
            ) : (
              <div className="md:col-span-2 rounded-lg border border-background-200/80 bg-background-50 p-8 text-center">
                <i className="ri-chat-new-line text-3xl text-foreground-400" />
                <p className="mt-3 text-sm text-foreground-600">这个话题的讨论区还很安静，来当第一个表达观点的人。</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <h2 className="text-xl md:text-3xl font-bold text-foreground-950">相关话题推荐</h2>
        <div className="mt-6 md:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {related.map((item) => (
            <TopicCard key={item.id} topic={item} />
          ))}
        </div>
      </section>
    </div>
  );
}